import React, { useEffect, useState } from "react";
import { FileSystemItem } from "types";
import Breadcrumb from "components/file-system/file-breadcrumb";
import { FolderStats } from "components/file-system/FolderStats";
import { FolderViewController } from "components/file-system/FolderViewController";
import { FolderListView } from "components/file-system/folder-view/FolderListView";
import { FolderGridView } from "components/file-system/folder-view/FolderGridView";
import SearchAndNew from "components/file-system/SearchAndNewFileOrFolder";
import { useConfirmation } from "components/dialog/confirmation";
import api from "api/axios";
import toast from "react-hot-toast";
import { FolderSymlink, Trash } from "lucide-react";
import { handleAxiosAndCaughtError } from "utils";
import { useLibraryContext } from "../../context/LibraryContext";
import { useSearchParams } from "react-router-dom";
import MoveItemsDialog from "./MoveItemsDialog";
import { useAuthContext } from "auth/AuthProvider";
import { PERMISSIONS } from "auth/permissions";
import { useTranslation } from "hooks/useTranslation";
import TooltipHorizon from "components/tooltip";
import { Library } from "constants/library";

interface FolderViewProps {
  sortedItems: FileSystemItem[];
  viewMode: "grid" | "list";
  sortOrder: "asc" | "desc";
  sortBy: "name" | "size";
  onSort: (by: "name" | "size") => void;
  onViewModeChange: (mode: "grid" | "list") => void;
  fetchFiles: () => Promise<void>;
  onWorkflowClick?: (id: string) => void;
  onNewWorkflow?: () => void;
  onDeleteWorkflow?: (id: string) => void;
}

const FolderView: React.FC<FolderViewProps> = ({
  sortedItems,
  viewMode,
  sortOrder,
  sortBy,
  fetchFiles,
  onSort,
  onViewModeChange,
  onWorkflowClick,
  onNewWorkflow,
  onDeleteWorkflow,
}) => {
  const [selectedFiles, setSelectedFiles] = useState<FileSystemItem[]>([]);
  const [isMoveDialogOpen, setIsMoveDialogOpen] = useState(false);
  const [, setSearchParams] = useSearchParams();
  const {
    query,
    setQuery,
    setCurrentFolderId,
    breadcrumb,
    setBreadcrumb,
    currentFolderId,
    isWorkflowLimitReached,
    isLoadingUsage,
  } = useLibraryContext();
  const { confirm } = useConfirmation();
  const { hasPermission } = useAuthContext();
  const { t } = useTranslation();

  const permissions = {
    canDeleteDocument: hasPermission(PERMISSIONS.DELETE_DOCUMENT),
    canUpdateDocument: hasPermission(PERMISSIONS.UPDATE_DOCUMENT),
  };

  const currentFolderName =
    breadcrumb.length > 0
      ? breadcrumb[breadcrumb.length - 1].name
      : t("library.root");
  const isWorkflowFolder = currentFolderName === Library.WORKFLOWS;

  const filteredItems =
    sortedItems?.filter((item) =>
      item.name.toLowerCase().includes(query.toLowerCase())
    ) || [];

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setSelectedFiles([]);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [sortedItems]);

  const handleMoveItems = async (destinationFolderId: string | null) => {
    try {
      const itemsToMove = selectedFiles
        .filter((item) => item.type === "Folder" || item.type === "Document")
        .reduce((acc, item) => {
          acc[item.id] = item.type as "Folder" | "Document";
          return acc;
        }, {} as Record<string, "Folder" | "Document">);

      if (Object.keys(itemsToMove).length === 0) {
        toast.error(t("library.workflowsCannotBeMoved"));
        return;
      }

      await api.post("/folders/move", {
        items: itemsToMove,
        destination_folder_id: destinationFolderId,
      });

      toast.success(t("library.itemsMovedSuccessfully"));
      await fetchFiles();
      setSelectedFiles([]);
      setIsMoveDialogOpen(false);
    } catch (err) {
      handleAxiosAndCaughtError(err);
    }
  };

  const handleDelete = async (item?: FileSystemItem) => {
    const itemsToDelete = item ? [item] : selectedFiles;
    if (itemsToDelete.length === 0) return;
    const isAllFolders = itemsToDelete.every((item) => item.type === "Folder");
    const isAllFiles = itemsToDelete.every((item) => item.type === "Document");
    let typeLabel = t("library.deleteItems", { count: itemsToDelete.length });
    if (isAllFolders)
      typeLabel =
        itemsToDelete.length > 1
          ? t("library.deleteFolders")
          : t("library.deleteFolder");
    else if (isAllFiles)
      typeLabel =
        itemsToDelete.length > 1
          ? t("library.deleteFiles")
          : t("library.deleteFile");

    const confirmed = await confirm({
      title: typeLabel,
      message: t("library.deleteConfirmMessage", { type: typeLabel }),
      confirmText: t("library.delete"),
      cancelText: t("common.cancel"),
      variant: "danger",
    });

    if (confirmed) {
      if (itemsToDelete.some((i) => i.type === "Workflow")) {
        const workflowToDelete = itemsToDelete.find(
          (i) => i.type === "Workflow"
        );
        if (onDeleteWorkflow && workflowToDelete) {
          onDeleteWorkflow(workflowToDelete.id);
          return;
        }
      }
      try {
        const itemsToDeleteFiltered = itemsToDelete.filter(
          (item) => item.type !== "Workflow"
        );
        if (itemsToDeleteFiltered.length !== itemsToDelete.length) {
          toast.error(t("library.cannotDeleteWorkflows"));
        }

        await Promise.all(
          itemsToDeleteFiltered.map((item) =>
            item?.type === "Document"
              ? api.delete(`/documents/${item.id}`)
              : api.delete(`/folders/${item.id}`)
          )
        );
        await fetchFiles();
      } catch (err) {
        handleAxiosAndCaughtError(err);
      }
    }
  };

  // When navigating into a folder, append to breadcrumb
  const handleNavigate = (item: FileSystemItem) => {
    switch (item.type) {
      case "Folder":
        setCurrentFolderId(item.id);
        setBreadcrumb([...breadcrumb, { name: item.name, id: item.id }]);
        break;
      case "Workflow":
        if (onWorkflowClick) onWorkflowClick(item.id);
        break;
      case "DeepExtract":
        setSearchParams({ deepExtract: item.id });
        break;
      default:
        setSearchParams({ file: item.id });
    }
  };

  // When clicking a breadcrumb, truncate and set currentFolderId
  const handleBreadcrumbClick = (id: string | null, index: number) => {
    if (id === null || index === -1) {
      setCurrentFolderId(null);
      setBreadcrumb([]);
    } else {
      setCurrentFolderId(id);
      setBreadcrumb(breadcrumb.slice(0, index + 1));
    }
  };

  const handleBack = () => {
    console.log("handleBack", breadcrumb);
    const parentIndex = breadcrumb.length - 2;
    const parent = breadcrumb[parentIndex];
    // if (parentIndex < 0) return;
    handleBreadcrumbClick(parent?.id ?? null, parentIndex);
  };

  const handleEdit = async (itemId: string, value: string) => {
    const existingItem = sortedItems.find(
      (item) =>
        item.id !== itemId && item.name.toLowerCase() === value.toLowerCase()
    );
    if (existingItem) {
      toast.error(t("library.itemAlreadyExists"));
      return;
    }
    const itemToEdit = sortedItems.find((item) => item.id === itemId);

    try {
      if (itemToEdit?.type === "Folder") {
        await api.put(`/folders/${itemId}`, {
          name: value,
        });
      } else if (itemToEdit?.type === "Workflow") {
        await api.patch(`/workflows/${itemId}/name`, {
          name: value,
        });
      } else {
        await api.patch(`/documents/${itemId}/name`, {
          name: value,
        });
      }
      toast.success(t("library.itemRenamedSuccessfully"));
      await fetchFiles();
    } catch (err) {
      handleAxiosAndCaughtError(err);
    }
  };

  return (
    <div className="  p-6  ">
      <div className="mx-auto max-w-7xl">
        {/* Header */}
        <div className="mb-6">
          <Breadcrumb
            breadcrumb={breadcrumb}
            onPathClick={handleBreadcrumbClick}
          />
          <div className="flex gap-3">
            <SearchAndNew
              parentId={currentFolderId}
              value={query}
              fetchFiles={fetchFiles}
              className="mb-6"
              onChange={setQuery}
              showAdd={!isWorkflowFolder}
            />
            {isWorkflowFolder &&
              (isWorkflowLimitReached ? (
                <TooltipHorizon
                  trigger={
                    <button
                      disabled={isWorkflowLimitReached || isLoadingUsage}
                      className="linear flex h-[45px] cursor-not-allowed items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-gray-400 px-4 text-white opacity-60 transition duration-200 dark:bg-gray-600"
                    >
                      {t("library.newWorkflow")}
                    </button>
                  }
                  content={
                    <span className="block max-w-[10rem] whitespace-normal text-center">
                      {t("library.workflowLimitReached")}
                    </span>
                  }
                  placement="top"
                  extra=""
                />
              ) : (
                <button
                  onClick={onNewWorkflow}
                  disabled={isLoadingUsage}
                  className={`linear flex h-[45px] items-center justify-center gap-2 whitespace-nowrap rounded-xl px-4 text-white transition duration-200 ${
                    isLoadingUsage
                      ? "cursor-not-allowed bg-gray-400 opacity-60 dark:bg-gray-600"
                      : "bg-brand-500 hover:bg-brand-600 active:bg-brand-700"
                  }`}
                >
                  {t("library.newWorkflow")}
                </button>
              ))}
          </div>
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 sm:text-3xl">
              {breadcrumb.length > 0
                ? breadcrumb[breadcrumb.length - 1].name
                : t("library.root")}
            </h1>
            <div className="flex justify-start md:justify-end">
              <FolderViewController
                onSort={(s) => onSort(s)}
                onViewModeChange={onViewModeChange}
                sortOrder={sortOrder}
                sortBy={sortBy}
                viewMode={viewMode}
              >
                {!isWorkflowFolder && (
                  <>
                    <button
                      onClick={() => setIsMoveDialogOpen(true)}
                      disabled={selectedFiles.length <= 0}
                      className={`flex items-center space-x-2 rounded-lg px-4 py-2 text-white shadow-sm transition-colors focus:outline-none focus:ring-2 ${
                        selectedFiles.length <= 0
                          ? "cursor-not-allowed border border-gray-300 bg-gray-200 text-gray-400 opacity-60 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-500"
                          : "bg-blue-500 hover:bg-blue-600 focus:ring-blue-200 dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-900"
                      }`}
                      aria-disabled={selectedFiles.length <= 0}
                      tabIndex={selectedFiles.length <= 0 ? -1 : 0}
                    >
                      <FolderSymlink className="h-4 w-4" />
                      <span>{t("library.move")}</span>
                    </button>
                    {permissions.canDeleteDocument && (
                      <button
                        onClick={() => handleDelete()}
                        disabled={selectedFiles.length <= 0}
                        className={`flex items-center space-x-2 rounded-lg px-4 py-2 text-white shadow-sm transition-colors focus:outline-none focus:ring-2 ${
                          selectedFiles.length <= 0
                            ? "cursor-not-allowed border border-gray-300 bg-gray-200 text-gray-400 opacity-60 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-500"
                            : "bg-red-500 hover:bg-red-600 focus:ring-red-200 dark:bg-red-600 dark:hover:bg-red-700 dark:focus:ring-red-900"
                        }`}
                        aria-disabled={selectedFiles.length <= 0}
                        tabIndex={selectedFiles.length <= 0 ? -1 : 0}
                      >
                        <Trash className="h-4 w-4" />
                        <span>{t("library.delete")}</span>
                      </button>
                    )}
                  </>
                )}
              </FolderViewController>
            </div>
          </div>
        </div>

        {/* File/Folder List and Stats or Empty State */}
        {/* {filteredItems.length > 0 ? ( */}
        <div className="rounded-lg border bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
          {viewMode === "list" ? (
            <FolderListView
              isFolderView
              isDocumentFileType={true}
              fileItems={filteredItems}
              selectedFiles={selectedFiles}
              setSelectedFiles={setSelectedFiles}
              onFileClick={(item) => {
                if (selectedFiles.some((f) => f.id === item.id)) {
                  setSelectedFiles(
                    selectedFiles.filter((f) => f.id !== item.id)
                  );
                } else {
                  setSelectedFiles([...selectedFiles, item]);
                }
              }}
              handleDelete={
                permissions.canDeleteDocument ? handleDelete : undefined
              }
              handleEdit={
                permissions.canUpdateDocument ? handleEdit : undefined
              }
              onSort={(s) => onSort(s)}
              willUpdateUrl
              handleNavigate={handleNavigate}
              onBack={breadcrumb.length > 0 ? handleBack : undefined}
              onDeleteWorkflow={onDeleteWorkflow}
              handleDeepExtract={true}
            />
          ) : (
            <FolderGridView
              fileItems={filteredItems}
              selectedFiles={selectedFiles}
              setSelectedFiles={setSelectedFiles}
              handleDelete={
                permissions.canDeleteDocument ? handleDelete : undefined
              }
              handleEdit={
                permissions.canUpdateDocument ? handleEdit : undefined
              }
              onFileClick={(item) => {
                if (selectedFiles.some((f) => f.id === item.id)) {
                  setSelectedFiles(
                    selectedFiles.filter((f) => f.id !== item.id)
                  );
                } else {
                  setSelectedFiles([...selectedFiles, item]);
                }
              }}
              willUpdateUrl
              handleNavigate={handleNavigate}
              isFolderView
              onDeleteWorkflow={onDeleteWorkflow}
              onBack={breadcrumb.length > 0 ? handleBack : undefined}
            />
          )}
        </div>
        {/* Stats */}
        <FolderStats fileItems={filteredItems} />
        {/* ) : ( */}
        {/* <div className="flex flex-col items-center justify-center rounded-lg border bg-white p-12 text-center shadow-sm dark:border-gray-700 dark:bg-gray-800">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              {query ? "No matching files or folders" : "This folder is empty"}
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {query
                ? "Try a different search term."
                : "Get started by creating a new file or folder."}
            </p>
          </div>
        )} */}
      </div>
      <MoveItemsDialog
        isOpen={isMoveDialogOpen}
        onClose={() => setIsMoveDialogOpen(false)}
        onMove={handleMoveItems}
        itemsToMove={selectedFiles}
        sourceFolderId={currentFolderId}
      />
    </div>
  );
};

export default FolderView;
